<template>
    <div
        class="conversation-item"
        :class="{
            'active': isActive,
            'unread': unreadCount > 0
        }"
        @click="$emit('select', conversation)"
    >
        <div class="avatar-container">
            <div class="avatar">
                <img
                    v-if="participantAvatar"
                    :src="participantAvatar"
                    :alt="participantName"
                />
                <span v-else class="avatar-initial">{{ avatarInitial }}</span>
            </div>
            <ChatOnlineStatus
                :is-online="isOnline"
                size="sm"
                class="online-indicator"
            />
        </div>

        <div class="conversation-content">
            <div class="conversation-header">
                <h4 class="participant-name">{{ participantName }}</h4>
                <span class="timestamp">{{ formattedTime }}</span>
            </div>

            <div class="conversation-preview">
                <div class="message-preview">
                    <span v-if="isTyping" class="typing-preview">
                        est en train d'écrire...
                    </span>
                    <template v-else>
                        <span v-if="lastMessageIsFromMe" class="you-prefix">Vous: </span>
                        <span class="preview-text">{{ lastMessagePreview }}</span>
                    </template>
                </div>

                <ChatUnreadBadge
                    v-if="unreadCount > 0"
                    :count="unreadCount"
                    size="sm"
                />
            </div>
        </div>
    </div>
</template>

<script>
import ChatOnlineStatus from './ChatOnlineStatus.vue';
import ChatUnreadBadge from './ChatUnreadBadge.vue';

export default {
    name: 'ChatConversationItem',
    components: {
        ChatOnlineStatus,
        ChatUnreadBadge
    },
    props: {
        conversation: {
            type: Object,
            required: true
        },
        currentUserId: {
            type: String,
            required: true
        },
        isActive: {
            type: Boolean,
            default: false
        },
        isOnline: {
            type: Boolean,
            default: false
        },
        isTyping: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        participant() {
            if (!this.conversation) return null;

            // Determine which participant is the other person
            const { seller, accountManager } = this.conversation;

            if (seller && seller._id === this.currentUserId) {
                return accountManager;
            }
            return seller;
        },
        participantName() {
            if (!this.participant) return 'Conversation';
            return this.participant.fullName ||
                   `${this.participant.firstName || ''} ${this.participant.lastName || ''}`.trim() ||
                   this.participant.email ||
                   'Utilisateur';
        },
        participantAvatar() {
            return (this.participant && this.participant.avatar) || null;
        },
        avatarInitial() {
            return this.participantName.charAt(0).toUpperCase();
        },
        lastMessage() {
            return (this.conversation && this.conversation.lastMessage) || null;
        },
        lastMessagePreview() {
            if (!this.lastMessage) return 'Aucun message';

            if (this.lastMessage.type === 'image') {
                return '📷 Image';
            }
            if (this.lastMessage.type === 'file') {
                return '📎 Fichier';
            }

            const content = this.lastMessage.content || '';
            return content.length > 50 ? content.substring(0, 50) + '...' : content;
        },
        lastMessageIsFromMe() {
            if (!this.lastMessage) return false;
            const sender = this.lastMessage.sender;
            const senderId = (sender && sender._id) || sender;
            return senderId === this.currentUserId;
        },
        formattedTime() {
            if (!this.lastMessage || !this.lastMessage.sentAt) return '';

            const messageDate = new Date(this.lastMessage.sentAt);
            const now = new Date();
            const diffMs = now - messageDate;
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffDays === 0) {
                return messageDate.toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit'
                });
            } else if (diffDays === 1) {
                return 'Hier';
            } else if (diffDays < 7) {
                return messageDate.toLocaleDateString('fr-FR', {
                    weekday: 'short'
                });
            } else {
                return messageDate.toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'short'
                });
            }
        },
        unreadCount() {
            if (!this.conversation) return 0;

            // Get unread count based on current user type
            const seller = this.conversation.seller;
            if (seller && seller._id === this.currentUserId) {
                return this.conversation.unreadBySeller || 0;
            }
            return this.conversation.unreadByAM || 0;
        }
    }
};
</script>

<style scoped>
.conversation-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    border-left: 3px solid transparent;
}

.conversation-item:hover {
    background-color: #f9fafb;
}

.conversation-item.active {
    background-color: #ecfdf5;
    border-left-color: #059669;
}

.conversation-item.unread {
    background-color: #f0fdf4;
}

.avatar-container {
    position: relative;
    flex-shrink: 0;
}

.avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    overflow: hidden;
}

.avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-initial {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: white;
    font-weight: 600;
    font-size: 18px;
}

.online-indicator {
    position: absolute;
    bottom: 2px;
    right: 2px;
}

.conversation-content {
    flex: 1;
    min-width: 0;
}

.conversation-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
}

.participant-name {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #111827;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.unread .participant-name {
    font-weight: 700;
}

.timestamp {
    font-size: 11px;
    color: #9ca3af;
    flex-shrink: 0;
    margin-left: 8px;
}

.unread .timestamp {
    color: #059669;
    font-weight: 600;
}

.conversation-preview {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.message-preview {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: #6b7280;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.unread .message-preview {
    color: #374151;
    font-weight: 500;
}

.typing-preview {
    color: #059669;
    font-style: italic;
}

.you-prefix {
    color: #9ca3af;
}

.preview-text {
    color: inherit;
}
</style>
